import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
  initializePublicWebAnalytics,
  trackPageView,
  trackPerformanceNavigation,
  trackRouteView,
} from "./analytics";
import {
  isPublicWebAnalyticsAllowed,
  onPublicWebAnalyticsConsentChanged,
} from "./analytics-consent";
import { normalizeAnalyticsRoute } from "./route-normalization";

function roundedNumber(value: unknown): number | undefined {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue < 0) return undefined;
  return Math.round(numberValue);
}

function reportNavigationTiming(route: string) {
  if (typeof window === "undefined" || typeof performance === "undefined") return;
  const navEntry = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!navEntry) return;
  trackPerformanceNavigation({
    route,
    type: navEntry.type,
    dns_ms: roundedNumber(navEntry.domainLookupEnd - navEntry.domainLookupStart),
    connect_ms: roundedNumber(navEntry.connectEnd - navEntry.connectStart),
    ttfb_ms: roundedNumber(navEntry.responseStart - navEntry.requestStart),
    response_ms: roundedNumber(navEntry.responseEnd - navEntry.responseStart),
    dom_ms: roundedNumber(navEntry.domContentLoadedEventEnd - navEntry.startTime),
    load_ms: roundedNumber(navEntry.loadEventEnd - navEntry.startTime),
  });
}

export function RouteAnalytics() {
  const location = useLocation();
  const analyticsAllowed = useSyncExternalStore(
    onPublicWebAnalyticsConsentChanged,
    isPublicWebAnalyticsAllowed,
    () => false,
  );
  const analyticsAllowedAtMount = useRef(analyticsAllowed).current;
  const initialRouteAtMount = useRef(normalizeAnalyticsRoute(location.pathname)).current;

  useEffect(() => {
    if (!analyticsAllowed) return;
    const route = normalizeAnalyticsRoute(location.pathname);
    // Start the tag in parallel, while the first-party endpoint records the
    // route immediately and remains the fallback when a tag is blocked.
    void initializePublicWebAnalytics();
    trackRouteView(route);
    trackPageView(route);
  }, [analyticsAllowed, location.pathname]);

  useEffect(() => {
    if (!analyticsAllowed || !analyticsAllowedAtMount) return;
    const timer = window.setTimeout(() => reportNavigationTiming(initialRouteAtMount), 1500);
    return () => window.clearTimeout(timer);
  }, [analyticsAllowed, analyticsAllowedAtMount, initialRouteAtMount]);

  return null;
}
